// URL-encoded shareable cart (Option A).
//
// A cart is compressed into a base64url-encoded JSON payload that lives in
// the ?share= query string. When the recipient loads the /cart page, we
// decode the payload back into cart state via CartContext.hydrateFromShare.
//
// Compact shape (short keys keep URLs short):
//   { v: 1, i: [{p: pkg_id, q: qty, o?: options}], f: "delivery"|"pickup", z?: "11433" }
//
// Notes:
// - We do NOT include prices — the recipient sees live pricing from the
//   current menu (role-aware). This also prevents tampering.
// - We do NOT include deliveryFee — it's re-derived from the ZIP on load.
// - Version guard (v: 1) lets us evolve the shape safely.

const CURRENT_VERSION = 1;

function toBase64Url(str) {
  // btoa handles latin1; use TextEncoder → binary string for full UTF-8 safety.
  const bytes = new TextEncoder().encode(str);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(b64url) {
  const padded = b64url.replace(/-/g, "+").replace(/_/g, "/") +
    "=".repeat((4 - (b64url.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

// Serialize a cart snapshot into a URL-safe token.
// `state` shape: { items, fulfillment, zip } — matches CartContext state.
export function encodeCart(state) {
  const items = Array.isArray(state?.items) ? state.items : [];
  const payload = {
    v: CURRENT_VERSION,
    i: items.map((it) => {
      const line = { p: String(it.package_id), q: Number(it.quantity) || 0 };
      if (it.selected_options && Object.keys(it.selected_options).length > 0) {
        line.o = it.selected_options;
      }
      return line;
    }),
    f: state?.fulfillment === "pickup" ? "pickup" : "delivery",
  };
  if (state?.zip) payload.z = String(state.zip);
  return toBase64Url(JSON.stringify(payload));
}

// Decode a token back into a normalized cart snapshot. Returns null on
// any parse/version/shape failure — callers should treat null as "ignore
// share param, use existing cart".
export function decodeCart(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parsed = JSON.parse(fromBase64Url(token));
    if (!parsed || parsed.v !== CURRENT_VERSION) return null;
    const rawItems = Array.isArray(parsed.i) ? parsed.i : [];
    const items = rawItems
      .filter((it) => it && typeof it.p === "string" && Number(it.q) > 0)
      .map((it) => {
        const line = { package_id: it.p, quantity: Math.min(999, Math.max(1, Math.round(Number(it.q)))) };
        if (it.o && typeof it.o === "object") line.selected_options = it.o;
        return line;
      });
    return {
      items,
      fulfillment: parsed.f === "pickup" ? "pickup" : "delivery",
      zip: typeof parsed.z === "string" ? parsed.z : "",
    };
  } catch (err) {
    if (typeof console !== "undefined") console.debug("shareCart: decode failed", err);
    return null;
  }
}

// Build the full absolute URL that lands the recipient on /cart with the
// share payload attached. Uses window.location.origin so it works in both
// preview and production without config.
export function buildShareUrl(state) {
  const token = encodeCart(state);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/cart?share=${token}`;
}
